import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import NotFound from './NotFound';
import Footer from '../components/Footer';
import { Header } from '../components/Header';
import Seo from '../components/Seo';
import blogPosts, { AUTHOR, lastModified, relatedPosts } from '../data/blogPosts';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <NotFound />;

  const url = `https://www.myfootballgame.com/blog/${post.slug}`;
  const related = relatedPosts(post.slug);
  const fmt = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.description,
        datePublished: post.date,
        dateModified: lastModified(post),
        image: 'https://www.myfootballgame.com/og-image.jpg',
        url,
        mainEntityOfPage: url,
        author: AUTHOR,
        publisher: {
          '@type': 'Organization',
          name: 'My Football Game',
          logo: { '@type': 'ImageObject', url: 'https://www.myfootballgame.com/logo.png' }
        }
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.myfootballgame.com/' },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.myfootballgame.com/blog' },
          { '@type': 'ListItem', position: 3, name: post.title, item: url }
        ]
      }
    ]
  };

  return (
    <motion.div
      className="w-full min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Seo
        title={`${post.title} | My Football Game Blog`}
        description={post.description}
        path={`/blog/${post.slug}`}
        jsonLd={jsonLd}
      />
      <div className="container mx-auto px-4">
        <Header />
      </div>

      <div className="max-w-3xl mx-auto px-4 pb-16">
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
          <nav className="text-sm text-white/50 mb-6">
            <Link to="/" className="hover:text-green-400">Home</Link>
            {' / '}
            <Link to="/blog" className="hover:text-green-400">Blog</Link>
            {' / '}
            <span className="text-white/70">{post.title}</span>
          </nav>

          <div className="text-xs text-white/50 mb-2">
            By Paulo · {post.updated ? <>Updated <time dateTime={post.updated}>{fmt(post.updated)}</time></> : <time dateTime={post.date}>{fmt(post.date)}</time>} · {post.readTime}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{post.title}</h1>
          <p className="text-white/70 text-lg mb-10">{post.description}</p>
        </motion.div>

        <div className="space-y-8">
          {post.sections.map((section, index) => (
            <motion.section
              key={section.heading}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.06 }}
            >
              <h2 className="text-2xl font-bold mb-3 text-green-400">{section.heading}</h2>
              <div
                className="text-white/80 space-y-3 [&_a]:text-green-400 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: section.html }}
              />
            </motion.section>
          ))}
        </div>

        {related.length > 0 && (
          <nav aria-label="Related guides" className="mt-12">
            <h2 className="text-xl font-bold mb-4">Keep reading</h2>
            <ul className="grid gap-3 md:grid-cols-3">
              {related.map((p) => (
                <li key={p.slug}>
                  <Link to={`/blog/${p.slug}`} className="block h-full rounded-lg border border-white/10 bg-white/[0.03] p-4 hover:bg-white/[0.06] transition-colors">
                    <span className="block font-bold text-sm mb-1">{p.title}</span>
                    <span className="block text-xs text-white/50">{p.readTime}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-green-900/30 to-emerald-900/30 p-6 rounded-xl border border-green-500/20 text-center"
        >
          <h3 className="text-xl font-bold mb-3">Put it into practice</h3>
          <p className="text-white/80 mb-6">
            Reading makes a manager smarter - matches make a manager better. Create your club free and test it on the pitch.
          </p>
          <Link
            to="/register"
            className="inline-block px-6 py-2.5 bg-[var(--accent)] rounded-lg font-medium transition-colors shadow-md"
          >
            Play My Football Game Free
          </Link>
        </motion.div>
      </div>

      <Footer />
    </motion.div>
  );
};

export default BlogPost;
